import { Allotment } from 'allotment'
import { useEffect, useState } from 'react'

import { compartmentsSelectors, fetchCompartments, saveCompartment } from '@/entities/compartments'
import { toggleCompartmentStatus } from '@/entities/compartments/thunks/toggleCompartmentStatus'
import { useDropdown } from '@/entities/dropdowns/libs/useDropdown'
import { DropdownOption } from '@/entities/dropdowns/models/DropdownOption'
import { Icon, IconButton, ToggleButton } from '@/shared/components'
import { DynamicForm } from '@/shared/components/DynamicForm/DynamicForm'
import { CustomGridToolbar } from '@/shared/components/GridTable/CustomGridToolbar'
import { GridTable } from '@/shared/components/GridTable/GridTable'
import { useToastContext } from '@/shared/components/Toast/ToastContext'
import { useAppDispatch, useAppSelector } from '@/shared/store'
import { pxToRem } from '@/shared/styles/themes'
import { faAdd, faClose, faEdit, faToggleOn } from '@fortawesome/pro-regular-svg-icons'
import { Divider, Grid, styled } from '@mui/material'
import { GridColDef, GridInputRowSelectionModel, GridValidRowModel } from '@mui/x-data-grid-premium'
import { EntityId } from '@reduxjs/toolkit'

import { getCompartmentFormDefinition, initialCompartmentFormValues } from './libs/getCompartmentFormDefinition'
import {
  StyledAddButton, StyledButtonsContainer, StyledContainer, StyledDescription, StyledHeader, StyledLeftPane, StyledRightPane,
} from './StyledOutletPage'

export type CompartmentsPageProps = {
  //
}

export const CompartmentsPage: React.FC<CompartmentsPageProps> = () => {
  const [rowSelectionModel, setRowSelectionModel] = useState<GridInputRowSelectionModel>()
  const [showDetails, setShowDetails] = useState(false)
  const [formType, setFormType] = useState<'add' | 'edit' | 'view'>('add')
  const [isSubmitting, setSubmitting] = useState(false)
  const [formValues, setFormValues] = useState<Record<string, unknown>>({})
  const [loading, setLoading] = useState(false)

  const dispatch = useAppDispatch()
  const compartments = useAppSelector(state => compartmentsSelectors.selectAll(state))
  const { data: groupOptions } = useDropdown('groups')
  const { data: attributeOptions } = useDropdown('vehicleAttributes')

  const { setToastContent } = useToastContext()

  // Fetch compartments from server
  const fetchCompartmentsFromServer = async () => {
    setLoading(true)
    await dispatch(fetchCompartments())
    setLoading(false)
  }

  useEffect(() => {
    fetchCompartmentsFromServer()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch])

  // Define the form fields and contents
  const formDefinition = getCompartmentFormDefinition({
    defaultValues: initialCompartmentFormValues,
    selectOptions: {
      groupId: groupOptions,
      attributeIds: attributeOptions,
    },
  })

  const handleToggleStatus = async (id: string) => {
    try {
      await dispatch(toggleCompartmentStatus({ id }))
      setToastContent({
        open: true,
        severity: 'success',
        title: 'Success',
        content: 'Compartment status updated successfully',
      })
    } catch (error) {
      setToastContent({
        open: true,
        severity: 'error',
        title: 'Error',
        content: 'Failed to update depot status',
      })
    }
  }

  const handleSubmit = (data: Record<string, unknown>) => {
    setSubmitting(true)

    const selectedCompartment = compartments.find(compartment => compartment.id === rowSelectionModel)

    if (formType === 'edit' && selectedCompartment) {
      // Handle edit compartment
      const editPayload = {
        ...selectedCompartment,
        ...data,
        groupId: (data.groupId as DropdownOption).value,
        attributeIds: (data.attributeIds as DropdownOption[]).map(a => a.value),
      }

      dispatch(saveCompartment({
        compartment: editPayload,
      })).unwrap().then((comp) => {
        setFormType('view')
        setRowSelectionModel(comp.id)
      })
    } else {
      // Handle add compartment
      const addPayload = {
        ...data,
        groupId: (data.groupId as DropdownOption).value,
        attributeIds: (data.attributeIds as DropdownOption[]).map(a => a.value),
      }

      dispatch(saveCompartment({
        compartment: addPayload,
      })).unwrap().then((comp) => {
        setFormType('view')
        setRowSelectionModel(comp.id)
      })
    }

    setSubmitting(false)
  }

  // Update form values when selectedId changes
  const resetForm = () => {
    if (formType === 'add') {
      setFormValues(initialCompartmentFormValues)
    } else {
      const selectedCompartment = compartments.find(compartment => compartment.id === rowSelectionModel)

      if (selectedCompartment) {
        setFormValues({
          ...selectedCompartment,
          groupId: groupOptions.find(g => g.value === selectedCompartment.groupId),
          attributeIds: attributeOptions.filter(a => selectedCompartment.attributeIds.includes(a.value)),
        })
      }
    }
  }

  useEffect(() => {
    if (!rowSelectionModel || !['edit', 'view'].includes(formType)) return

    resetForm()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowSelectionModel, compartments, formType, setFormValues])

  // Define the columns for the grid
  const rows = compartments.map(item => ({
    ...item,
    isSelectable: true,
  }))

  const columns: GridColDef[] = [
    {
      field: 'groupId',
      headerName: 'Group',
      width: 150,
      valueGetter: (value) => {
        return groupOptions.find(g => g.value === value)?.label || ''
      },
    },
    {
      field: 'name',
      headerName: 'Name',
      width: 200,
    },
    {
      field: 'weightCapacity',
      headerName: 'Weight',
      width: 120,
    },
    {
      field: 'volumeCapacity',
      headerName: 'Volume',
      width: 120,
    },
    {
      field: 'attributeIds',
      headerName: 'Attributes',
      width: 200,
      valueGetter: (value: EntityId[]) => {
        if (!value || !Array.isArray(value)) return ''

        return value
          .map(id => attributeOptions.find(a => a.value === id)?.label)
          .filter(Boolean)
          .join(', ')
      },
    },
    {
      field: 'isActive',
      headerName: 'Status',
      width: 100,
      valueGetter: (value) => value ? 'Active' : 'Disabled',
    },
  ]

  const onViewRowDetail = (row: GridValidRowModel) => {
    setFormType('view')
    setShowDetails(true)
    setRowSelectionModel(row.id)
  }

  const onHandleAdd = () => {
    setFormType('add')
    setShowDetails(true)
    setRowSelectionModel(undefined)
    setFormValues(initialCompartmentFormValues)
  }

  const handleCloseDetail = () => {
    setRowSelectionModel(undefined)
    setShowDetails(false)
  }
  
  return (
    <StyledContainer>
      <Allotment separator={true} defaultSizes={[100, 100]}>
        <Allotment.Pane>
          <StyledLeftPane>
            <Grid container direction={'row'} alignItems={'flex-start'} justifyContent={'space-between'} wrap="nowrap">
              <Grid item>
                <StyledHeader variant="body1">Compartments</StyledHeader>
                <StyledDescription>
                  Configure compartment details and capacity. Assign attributes for specialized cargo handling.
                </StyledDescription>
              </Grid>
              <Grid item>
                <StyledAddButton
                  primary
                  startIcon={<Icon icon={faAdd} />}
                  onClick={onHandleAdd}
                  sx={{ minWidth: pxToRem(180) }}
                >
                  New Compartment
                </StyledAddButton>
              </Grid>
            </Grid>
            <Divider sx={{ marginTop: pxToRem(16) }} />
            <StyledGridTable
              rows={rows}
              columns={columns}
              loading={loading}
              isDragDropEnabled={false}
              rowSelectionModel={rowSelectionModel}
              onRowClick={onViewRowDetail}
              initialState={{
                filter: {
                  filterModel: {
                    items: [{
                      field: 'isActive', operator: 'equals', value: 'Active',
                    }],
                  },
                },
              }}
              slots={{
                toolbar: CustomGridToolbar,
              }}
              sx={{
                '.MuiDataGrid-filler--pinnedRight': { display: 'none' },
              }}
            />
          </StyledLeftPane>
        </Allotment.Pane>
        <Allotment.Pane visible={showDetails} minSize={360}>
          <StyledRightPane $visible={showDetails}>
            <Grid container direction={'row'} alignItems={'flex-start'} justifyContent={'space-between'} wrap="nowrap">
              <Grid item>
                <StyledHeader variant="body1" sx={{ paddingLeft: 0 }}>
                  {formType === 'add' && 'Add a new compartment'}
                  {formType === 'edit' && `Edit ${formValues?.name || ''} details`}
                  {formType === 'view' && `View ${formValues?.name || ''} details`}
                </StyledHeader>
              </Grid>
              <StyledButtonsContainer item>
                <ToggleButton
                  value="active"
                  primary
                  text
                  selected={false}
                  disabled={formType === 'add'}
                  onChange={() => handleToggleStatus(rowSelectionModel as string)}
                >
                  <Icon icon={faToggleOn} sx={{ fontSize: pxToRem(14), marginRight: pxToRem(4) }} />
                  Toggle Status
                </ToggleButton>
                <ToggleButton
                  value="checked"
                  primary
                  text
                  disabled={formType === 'add'}
                  selected={formType === 'edit'}
                  onChange={() => {
                    resetForm()
                    setFormType((prev) => prev === 'edit' ? 'view' : 'edit')
                  }}
                  sx={{ minWidth: pxToRem(100) }}
                >
                  <Icon icon={faEdit} sx={{ fontSize: pxToRem(14), marginRight: pxToRem(4) }} />
                  {formType === 'edit' ? 'Cancel Edit' : 'Edit Compartment'}
                </ToggleButton>

                <IconButton size="small" onClick={() => handleCloseDetail()}>
                  <Icon icon={faClose} sx={{ fontSize: pxToRem(18) }} />
                </IconButton>
              </StyledButtonsContainer>
            </Grid>
            <DynamicForm
              {...formDefinition}
              initialValues={formValues}
              onSubmit={handleSubmit}
              isReadOnly={isSubmitting || !showDetails || formType === 'view'}
              onCancel={() => {
                resetForm()

                if (formType === 'add') {
                  setShowDetails(false)
                }

                setFormType('view')
              }}
              slotProps={{
                submitButton: {
                  disabled: isSubmitting,
                },
              }}
            />
          </StyledRightPane>
        </Allotment.Pane>
      </Allotment>
    </StyledContainer>
  )
}

const StyledGridTable = styled(GridTable)`
  min-height: ${pxToRem(360)};
  height: calc(100% - ${pxToRem(86)});
`